import { Helmet } from "react-helmet-async";
import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ChevronDown, GraduationCap, Briefcase, LineChart, Users, Rocket, MapPin, RefreshCw, Brain } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";


/* ─── Ticker items ─────────────────────────────────────── */
const tickerItems = [
  "AI Transformation",
  "Enterprise Automation",
  "Executive AI Readiness",
  "Startup AI Growth",
  "Workforce Productivity",
  "Practical AI Skilling",
];

/* ─── Hero stat cards ─────────────────────────────────── */
const heroStats = [
  {
    value: "25+",
    label: "Professionals Upskilled",
    bg: "bg-[#5C2248]",
    text: "text-white",
    subText: "text-white/70",
  },
  {
    value: "10",
    label: "Banks Trained in SA",
    bg: "bg-[#E8718A]",
    text: "text-white",
    subText: "text-white/80",
  },
  {
    value: "2",
    label: "Countries Reached",
    bg: "bg-[#C4879A]",
    text: "text-white",
    subText: "text-white/80",
  },
  {
    value: "15+",
    label: "Business Leaders Trained",
    bg: "bg-[#7D3260]/60",
    text: "text-white",
    subText: "text-white/70",
  },
];

/* ─── Program cards ───────────────────────────────────── */
const programs = [
  {
    number: "01",
    icon: Briefcase,
    title: "Corporates and Enterprises",
    description:
      "AI transformation, workforce productivity, automation, and enterprise innovation solutions for organizations.",
    tag: "Teams · Enterprise",
    bg: "#6A2452",           // deep berry
    numColor: "rgba(255,255,255,0.08)",
    tagBg: "rgba(255,255,255,0.15)",
  },
  {
    number: "02",
    icon: LineChart,
    title: "Business Leaders & Executives",
    description:
      "Executive AI readiness, leadership advisory, strategic AI adoption, and decision making enablement.",
    tag: "Executives · Leaders",
    bg: "#7D3260",           // berry purple
    numColor: "rgba(255,255,255,0.08)",
    tagBg: "rgba(255,255,255,0.15)",
  },
  {
    number: "03",
    icon: Rocket,
    title: "Entrepreneurs & Startups",
    description:
      "AI driven business growth, innovation support, digital transformation, and startup enablement programs.",
    tag: "Startups · Founders",
    bg: "#E8718A",           // warm pink
    numColor: "rgba(255,255,255,0.12)",
    tagBg: "rgba(255,255,255,0.20)",
  },
  {
    number: "04",
    icon: Users,
    title: "Professionals & Teams",
    description:
      "Practical AI skilling, Copilot training, productivity enhancement, and future ready workforce development.",
    tag: "Workforce · Teams",
    bg: "#C4879A",           // mauve/nude
    numColor: "rgba(255,255,255,0.12)",
    tagBg: "rgba(255,255,255,0.20)",
  },
];

/* ─── Impact snapshot cards (home page) ──────────────── */
const homeImpactCards = [
  {
    icon: Briefcase,
    badge: "Upskilling",
    badgeBg: "rgba(232, 113, 138, 0.25)",
    title: "Professionals upskilled",
    description: "Hands-on AI and Copilot training for corporate teams and developers.",
    stat: "25+",
    statLabel: "professionals trained in key tracks",
    iconBg: "#7D3260",
  },
  {
    icon: Rocket,
    badge: "Enterprise depth",
    badgeBg: "rgba(125, 50, 96, 0.35)",
    title: "Banks in South Africa",
    description: "Custom Copilot skilling and AI readiness training delivered to top-tier financial teams.",
    stat: "10",
    statLabel: "financial institutions trained",
    iconBg: "#E8718A",
  },
  {
    icon: MapPin,
    badge: "Reach",
    badgeBg: "rgba(242, 169, 155, 0.25)",
    title: "Active Hubs",
    description: "Establishing local presence and training cohorts starting in Kenya and South Africa.",
    stat: "2",
    statLabel: "countries with active programs",
    iconBg: "#7D3260",
  },
];

export default function Home() {
  return (
    <>
      <Helmet>
        <title>AISWERVE AFRICA Network – Corporate AI & Digital Transformation</title>
        <meta
          name="description"
          content="AISWERVE AFRICA equips corporate professionals, developers, and business leaders with practical AI and digital skills for the evolving future of work."
        />
        <meta property="og:title" content="AISWERVE AFRICA Network – Corporate AI & Digital Transformation" />
        <meta
          property="og:description"
          content="AISWERVE AFRICA equips corporate professionals, developers, and business leaders with practical AI and digital skills for the evolving future of work."
        />
        <meta property="og:url" content="https://aiswerveafrica.com/" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://aiswerveafrica.com/og-image.png" />
        <meta name="twitter:title" content="AISWERVE AFRICA Network – Corporate AI & Digital Transformation" />
        <meta
          name="twitter:description"
          content="AISWERVE AFRICA equips corporate professionals, developers, and business leaders with practical AI and digital skills for the evolving future of work."
        />
        <link rel="canonical" href="https://aiswerveafrica.com/" />
      </Helmet>

      <div className="min-h-screen">

        {/* ══════════════════════════════════════════
            HERO SECTION — Deep Berry, no image
        ══════════════════════════════════════════ */}
        <section
          className="relative min-h-screen flex flex-col overflow-hidden"
          style={{ backgroundColor: "#6A2452" }}
        >
          {/* ── Decorative background circles ─────── */}
          <div className="pointer-events-none absolute inset-0 overflow-hidden">
            {/* Large circle top-right */}
            <div
              className="absolute rounded-full"
              style={{
                width: "420px",
                height: "420px",
                top: "-80px",
                right: "-60px",
                background: "rgba(125, 50, 96, 0.55)",
              }}
            />
            {/* Medium circle mid-right */}
            <div
              className="absolute rounded-full"
              style={{
                width: "260px",
                height: "260px",
                top: "120px",
                right: "200px",
                background: "rgba(196, 135, 154, 0.25)",
              }}
            />
            {/* Small circle lower-left */}
            <div
              className="absolute rounded-full"
              style={{
                width: "160px",
                height: "160px",
                bottom: "140px",
                left: "-50px",
                background: "rgba(125, 50, 96, 0.35)",
              }}
            />
          </div>

          {/* ── Main hero content ──────────────────── */}
          <div className="relative z-10 flex-1 container mx-auto px-6 lg:px-8 pt-16 pb-8 flex items-center">
            <div className="w-full grid lg:grid-cols-2 gap-12 lg:gap-8 items-center">

              {/* LEFT — copy */}
              <div className="flex flex-col gap-6">
                {/* Badge pill */}
                {/* <div className="inline-flex items-center self-start gap-2 px-4 py-2 rounded-full border border-white/25 bg-white/10 backdrop-blur-sm">
                  <span className="w-2 h-2 rounded-full bg-[#E8718A] flex-shrink-0" />
                  <span className="font-dm-sans text-sm text-white/90 tracking-wide">
                    AI · Automation · Digital Leadership
                  </span>
                </div> */}

                {/* Heading — mixed typography */}
                <h1 className="font-cormorant font-semibold leading-[1.08] text-white">
                  <span className="block text-5xl md:text-6xl lg:text-7xl">
                    Building Africa's
                  </span>
                  {/* Italic warm-pink line */}
                  <span
                    className="block text-5xl md:text-6xl lg:text-7xl italic"
                    style={{ color: "#E8718A" }}
                  >
                    next generation
                  </span>
                  <span className="block text-5xl md:text-6xl lg:text-7xl">
                    of{" "}
                    <span style={{ color: "#C4879A" }}>industry leaders</span>
                  </span>
                </h1>

                {/* Subtext */}
                <p className="font-dm-sans text-base md:text-lg text-white/70 max-w-md leading-relaxed">
                  AI transformation, executive strategy, and digital productivity across
                  Africa — going deep, not just broad.
                </p>

                {/* CTAs */}
                <div className="flex flex-wrap items-center gap-4 mt-2">
                  <Link
                    to="/programs"
                    className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-white/35 text-white font-dm-sans text-sm font-medium hover:bg-white/10 transition-colors duration-200"
                  >
                    Explore programs
                  </Link>
                  <Link
                    to="/impact"
                    className="inline-flex items-center gap-2 text-white/85 font-dm-sans text-sm font-medium hover:text-white transition-colors duration-200"
                  >
                    Our impact
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </div>

              {/* RIGHT — 2×2 stat cards */}
              <div className="grid grid-cols-2 gap-3 lg:gap-4">
                {heroStats.map((stat, i) => (
                  <div
                    key={i}
                    className={`${stat.bg} rounded-2xl p-5 lg:p-6 flex flex-col gap-1 backdrop-blur-sm`}
                  >
                    <span
                      className={`font-cormorant font-bold text-4xl lg:text-5xl ${stat.text} leading-none`}
                    >
                      {stat.value}
                    </span>
                    <span className={`font-dm-sans text-sm ${stat.subText} leading-snug mt-1`}>
                      {stat.label}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* ── Scroll indicator ──────────────────── */}
          <div className="relative z-10 flex justify-center pb-6">
            <button
              onClick={() =>
                document.getElementById("programs")?.scrollIntoView({ behavior: "smooth" })
              }
              aria-label="Scroll to programs"
              className="w-9 h-9 rounded-full border border-white/30 flex items-center justify-center animate-bounce hover:bg-white/10 hover:border-white/60 transition-colors duration-200 cursor-pointer"
            >
              <ChevronDown className="w-4 h-4 text-white/70" />
            </button>
          </div>

          {/* ── Ticker / Marquee ──────────────────── */}
          <div
            className="relative z-10 overflow-hidden border-t border-white/15"
            style={{ backgroundColor: "#E8718A" }}
          >
            <div
              className="flex whitespace-nowrap animate-[marquee_22s_linear_infinite] py-3"
              style={{ width: "max-content" }}
            >
              {[...tickerItems, ...tickerItems].map((item, i) => (
                <span
                  key={i}
                  className="font-dm-sans text-sm font-semibold text-white tracking-wide mx-8 flex items-center gap-3"
                >
                  {item}
                  {i < tickerItems.length * 2 - 1 && (
                    <span className="inline-block w-1.5 h-1.5 rounded-full bg-white/60 ml-8" />
                  )}
                </span>
              ))}
            </div>
          </div>
        </section>

        {/* ══════════════════════════════════════════
            PROGRAMS SECTION — numbered cards
        ══════════════════════════════════════════ */}
        <section id="programs" className="py-20 bg-background">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="font-cormorant font-semibold text-3xl md:text-5xl text-foreground mb-4">
                Our Programs
              </h2>
              <p className="font-dm-sans text-lg text-muted-foreground max-w-2xl mx-auto">
                Deep, hands-on training that builds real-world skills — not just awareness.
              </p>
            </div>

            {/* Numbered card grid */}
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {programs.map((program) => (
                <div
                  key={program.number}
                  className="relative rounded-2xl p-7 flex flex-col gap-4 overflow-hidden group hover:-translate-y-1 transition-transform duration-300"
                  style={{ backgroundColor: program.bg }}
                >
                  {/* Faded number watermark */}
                  <span
                    className="absolute top-4 left-6 font-cormorant font-bold text-7xl leading-none select-none pointer-events-none"
                    style={{ color: program.numColor }}
                  >
                    {program.number}
                  </span>

                  {/* Icon */}
                  <div className="relative z-10 mt-6">
                    <program.icon className="w-7 h-7 text-white/80" />
                  </div>

                  {/* Title */}
                  <h3 className="relative z-10 font-cormorant font-semibold text-2xl text-white leading-tight">
                    {program.title}
                  </h3>

                  {/* Description */}
                  <p className="relative z-10 font-dm-sans text-sm text-white/75 leading-relaxed flex-1">
                    {program.description}
                  </p>

                  {/* Tag badge */}
                  <div className="relative z-10">
                    <span
                      className="inline-block font-dm-sans text-xs text-white/90 px-3 py-1.5 rounded-full"
                      style={{ backgroundColor: program.tagBg }}
                    >
                      {program.tag}
                    </span>
                  </div>
                </div>
              ))}
            </div>

            {/* View all programs CTA */}
            <div className="text-center mt-12">
              <Link
                to="/programs"
                className="inline-flex items-center gap-2 font-dm-sans text-sm font-medium text-[#7D3260] hover:text-[#6A2452] transition-colors"
              >
                View all programs <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </section>


        {/* ══════════════════════════════════════════
            IMPACT SNAPSHOT — deep berry, 2×2 grid
        ══════════════════════════════════════════ */}
        <section
          className="relative py-20 overflow-hidden"
          style={{ backgroundColor: "#6A2452" }}
        >
          {/* Decorative circles */}
          <div className="pointer-events-none absolute inset-0 overflow-hidden">
            <div
              className="absolute rounded-full"
              style={{ width: "300px", height: "300px", top: "-60px", right: "-40px", background: "rgba(125,50,96,0.40)" }}
            />
            <div
              className="absolute rounded-full"
              style={{ width: "140px", height: "140px", bottom: "40px", left: "-30px", background: "rgba(196,135,154,0.20)" }}
            />
          </div>

          <div className="relative z-10 container mx-auto px-6 lg:px-8">
            {/* Heading */}
            <div className="mb-12">
              <h2 className="font-cormorant font-semibold text-4xl md:text-5xl text-white leading-tight mb-2">
                Africa building for Africa
              </h2>
              <p className="font-dm-sans text-sm text-white/55">
                Real programs. Real outcomes. Across the continent.
              </p>
            </div>

            {/* Grid */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {homeImpactCards.map((card, i) => (
                <div
                  key={i}
                  className="rounded-2xl p-6 flex flex-col gap-4 hover:-translate-y-0.5 transition-transform duration-300"
                  style={{
                    backgroundColor: "rgba(255,255,255,0.07)",
                    border: "1px solid rgba(255,255,255,0.10)",
                    backdropFilter: "blur(8px)",
                  }}
                >
                  {/* Icon + badge */}
                  <div className="flex items-center justify-between">
                    <div
                      className="w-11 h-11 rounded-xl flex items-center justify-center"
                      style={{ backgroundColor: card.iconBg }}
                    >
                      <card.icon className="w-5 h-5 text-white" />
                    </div>
                    <span
                      className="font-dm-sans text-xs font-semibold text-white/90 px-3 py-1 rounded-full"
                      style={{ backgroundColor: card.badgeBg, border: "1px solid rgba(255,255,255,0.15)" }}
                    >
                      {card.badge}
                    </span>
                  </div>

                  {/* Title + description */}
                  <div>
                    <h3 className="font-cormorant font-semibold text-xl text-white mb-1">
                      {card.title}
                    </h3>
                    <p className="font-dm-sans text-sm text-white/60 leading-relaxed">
                      {card.description}
                    </p>
                  </div>

                  {/* Stat */}
                  <div>
                    <div
                      className="font-cormorant font-bold text-4xl md:text-5xl leading-none"
                      style={{ color: "#E8718A" }}
                    >
                      {card.stat}
                    </div>
                    <div className="font-dm-sans text-xs text-white/45 mt-1">
                      {card.statLabel}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* CTA */}
            <div className="mt-10 flex justify-center">
              <Link
                to="/impact"
                className="inline-flex items-center gap-2 px-8 py-3 rounded-full font-dm-sans text-sm font-semibold text-white border border-white/30 hover:bg-white/10 transition-colors"
              >
                View our full impact <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </section>

        {/* ══════════════════════════════════════════
            OUR MISSION — split layout
        ══════════════════════════════════════════ */}
        <section className="py-20" style={{ backgroundColor: "#FDF5F3" }}>
          <div className="container mx-auto px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 items-center">

              {/* LEFT — mission copy */}
              <div>
                <p
                  className="font-dm-sans text-xs font-bold tracking-widest uppercase mb-5"
                  style={{ color: "#E8718A" }}
                >
                  Our Mission
                </p>
                <h2 className="font-cormorant font-semibold text-4xl md:text-5xl text-foreground leading-tight mb-6">
                  Africa as an AI{" "}
                  <em style={{ color: "#E8718A", fontStyle: "italic" }}>producer</em>,
                  <br />
                  not just a consumer
                </h2>
                <p className="font-dm-sans text-base text-muted-foreground leading-relaxed mb-8 max-w-md">
                  We believe the next generation of AI solutions for African problems will be
                  built by Africans — with depth, not just breadth.
                </p>
                {/* Sector pills */}
                <div className="flex flex-wrap gap-2">
                  {["Enterprises", "Business Leaders", "Startups", "Workforce Teams"].map((tag) => (
                    <span
                      key={tag}
                      className="font-dm-sans text-xs font-medium px-4 py-2 rounded-full border"
                      style={{ borderColor: "#D4AFA4", color: "#6A2452" }}
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>

              {/* RIGHT — 3 feature cards */}
              <div className="flex flex-col gap-4">
                {[
                  {
                    icon: Brain,
                    iconBg: "rgba(125,50,96,0.12)",
                    iconColor: "#7D3260",
                    title: "Depth over breadth",
                    desc: "We train for mastery. Every track ends with a real-world project, not just a certificate.",
                  },
                  {
                    icon: Users,
                    iconBg: "rgba(196,135,154,0.15)",
                    iconColor: "#C4879A",
                    title: "Inclusive by design",
                    desc: "Gender inclusion, multilingual delivery, and accessible formats across the continent.",
                  },
                  {
                    icon: RefreshCw,
                    iconBg: "rgba(232,113,138,0.12)",
                    iconColor: "#E8718A",
                    title: "Employer-linked",
                    desc: "Industry feedback loops keep every curriculum current with what employers actually need.",
                  },
                ].map((item, i) => (
                  <div
                    key={i}
                    className="flex items-start gap-4 rounded-2xl p-5"
                    style={{
                      backgroundColor: "white",
                      border: "1px solid #EDE0DC",
                    }}
                  >
                    <div
                      className="w-11 h-11 rounded-xl flex-shrink-0 flex items-center justify-center"
                      style={{ backgroundColor: item.iconBg }}
                    >
                      <item.icon className="w-5 h-5" style={{ color: item.iconColor }} />
                    </div>
                    <div>
                      <h3 className="font-cormorant font-semibold text-lg text-foreground mb-1">
                        {item.title}
                      </h3>
                      <p className="font-dm-sans text-sm text-muted-foreground leading-relaxed">
                        {item.desc}
                      </p>
                    </div>
                  </div>
                ))}
              </div>

            </div>
          </div>
        </section>

        {/* ══════════════════════════════════════════
            BOTTOM CTA — warm pink, join the network
        ══════════════════════════════════════════ */}
        <section
          className="relative py-24 overflow-hidden"
          style={{ backgroundColor: "#E8718A" }}
        >
          {/* Decorative blobs */}
          <div className="pointer-events-none absolute inset-0 overflow-hidden">
            <div
              className="absolute rounded-full"
              style={{ width: "280px", height: "280px", bottom: "-60px", left: "-60px", background: "rgba(125,50,96,0.35)" }}
            />
            <div
              className="absolute rounded-full"
              style={{ width: "160px", height: "160px", top: "-40px", right: "15%", background: "rgba(74,26,58,0.25)" }}
            />
          </div>

          <div className="relative z-10 container mx-auto px-6 lg:px-8 text-center">
            <h2 className="font-cormorant font-semibold text-4xl md:text-6xl text-white mb-4 leading-tight">
              Ready to join
              <br />
              <em style={{ fontStyle: "italic" }}>the network?</em>
            </h2>
            <p className="font-dm-sans text-base text-white/80 max-w-xl mx-auto mb-10">
              Whether you're a business leader, professional, startup, or enterprise — there's a place for you.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/programs"
                className="inline-flex items-center gap-2 px-8 py-3 rounded-full font-dm-sans text-sm font-semibold text-white border-2 border-white hover:bg-white hover:text-[#E8718A] transition-colors"
              >
                Explore Programs <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 px-8 py-3 rounded-full font-dm-sans text-sm font-semibold text-white border border-white/40 hover:bg-white/15 transition-colors"
              >
                Get in Touch
              </Link>
            </div>
          </div>
        </section>

      </div>
    </>
  );
}